import type { BuildAndBuyStateType } from "../redux/store";
import { showMonthlyRatesInBasket } from "./typedUtils";
import { showTradeIn } from "./settings/bnbSettingUtils";
import { showBnbInsurance, showExternalFrame } from "./settings/bnbFinanceSettingUtils";
import { INSURANCE_CALCULATOR } from "../../../../common-deprecated/constants";
import { formatPrice } from "../../../../common-deprecated/Globalize";
import { getSelectedFinancePackEquipment } from "../../../shared-logic/utils/packUtils";
import type { PackType, PriceType } from "../../../../common-deprecated/types/CommonTypes";
import { addPrices } from "../../../shared-logic/utils/priceUtils";
import { canNavigateToBuyStep } from "../../../shared-logic/utils/checkoutUtils";
import type { FinanceRateStateType } from "../redux/reducers/FinanceRateReducer";
import type { FinancePackConfigurablesType } from "../../../shared-logic/types/FinanceTypes";
import { isRetailer } from "../../../../common-deprecated/utils";

type ReturnType = {
    showTradeIn: boolean;
    showInsurance: boolean;
    showFinance: boolean;
};

export const enabledServicesSelector = (state: BuildAndBuyStateType): ReturnType => {
    const { commonSettings, bnbSettings, finance, car, navigation, utils } = state;

    return {
        showFinance:
            !isRetailer(commonSettings) &&
            showMonthlyRatesInBasket(commonSettings, bnbSettings, finance, navigation.step, car.model.internalCode),
        showTradeIn:
            !isRetailer(commonSettings) &&
            showTradeIn(bnbSettings, commonSettings, utils.financeOption, finance.info.productName),
        showInsurance:
            !isRetailer(commonSettings) &&
            showExternalFrame(commonSettings, bnbSettings, INSURANCE_CALCULATOR, car) &&
            showBnbInsurance(commonSettings, bnbSettings, car),
    };
};

export const getSelectedFinancePackEquipmentSelector =
    (pack: PackType) =>
    (
        state: BuildAndBuyStateType,
    ): { standard: FinancePackConfigurablesType[]; optional: FinancePackConfigurablesType[] } => {
        const { basket, utils, items } = state;
        const { financeOption } = utils;
        return getSelectedFinancePackEquipment(pack, basket, items, financeOption);
    };

export const getSelectedFinancePacksEquipmentSelectorTotal = (state: BuildAndBuyStateType): number => {
    const { basket, utils, items } = state;
    const { financeOption } = utils;
    return state.items.financePacks.reduce((acc, financePack) => {
        const selectedPackEquipment = getSelectedFinancePackEquipment(financePack, basket, items, financeOption);
        acc += selectedPackEquipment.standard.length + selectedPackEquipment.optional.length;
        return acc;
    }, 0);
};

export const getSelectedFinancePacksSelector = (state: BuildAndBuyStateType): PackType[] => {
    const { basket, items, utils } = state;
    const { financeOption } = utils;

    if (utils.financeOption === "cash") {
        return items.financePacks.filter((financePack) => {
            const equipment = getSelectedFinancePackEquipment(financePack, basket, items, financeOption);
            return equipment.standard.length > 0 || equipment.optional.length > 0;
        });
    } else {
        return basket.financePacks;
    }
};

export const getTotalServicePriceSelector = (state: BuildAndBuyStateType): PriceType => {
    const selectedFinancePacks = getSelectedFinancePacksSelector(state);
    const { basket, items, utils } = state;
    const { financeOption } = utils;

    const selectedFinancePackEquipments = selectedFinancePacks.reduce((prevEquipment, pack) => {
        const selectedFinancePackEquipment = getSelectedFinancePackEquipment(pack, basket, items, financeOption);
        return [...prevEquipment, ...selectedFinancePackEquipment.standard, ...selectedFinancePackEquipment.optional];
    }, [] as FinancePackConfigurablesType[]);

    return addPrices(
        selectedFinancePackEquipments.reduce((totalPrices, financePackEquipment) => {
            return [...totalPrices, financePackEquipment.price];
        }, [] as PriceType[]),
    );
};

export type taxLabelReturnType = {
    label: string;
    key: string;
    prices: string[];
    disclaimer?: string;
};

export const taxLabelsSelector = (state: BuildAndBuyStateType): taxLabelReturnType[] | null => {
    const { commonSettings, bnbSettings, externalPrice } = state;

    if (externalPrice.calculatedPrice.extraCosts && externalPrice.calculatedPrice.extraCosts?.length > 0) {
        return bnbSettings.taxFormats
            .map((tax) => {
                // Check if key exists in externalPrice
                const externalPriceTax = externalPrice.calculatedPrice.extraCosts?.find(
                    (extraCost) => extraCost.key === tax.key,
                );

                if (externalPriceTax) {
                    return {
                        label: tax.label,
                        key: tax.key,
                        disclaimer: tax.disclaimer,
                        prices: externalPriceTax.values.map((value) =>
                            formatPrice(value.amount, commonSettings.culture.name),
                        ),
                    };
                }
                return null;
            })
            .filter(Boolean) as taxLabelReturnType[];
    } else return null;
};

export const enableTaxAndCostsSelector = (state: BuildAndBuyStateType): boolean => {
    const { commonSettings, bnbSettings, finance, utils, externalPrice, basket } = state;
    return (
        commonSettings.country === "fr" &&
        (commonSettings.query.enableTaxAndCosts ||
            canNavigateToBuyStep<FinanceRateStateType>(
                state.bnbSettings.enableBuy,
                state.bnbSettings.allowedCheckoutFinanceProduct,
                finance,
                utils.financeOption,
                basket,
            )) &&
        !!bnbSettings.taxFormats.length &&
        externalPrice.enabled &&
        !externalPrice.error
    );
};
